import { Icon } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { X } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { StorageKeys } from '~/atoms/StorageKeys'
import { Text } from '~/components/core'
import { times } from '~/constants/config'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useNotifications } from '~/hooks/useNotifications'
import {
  BannerIconButton,
  BannerTextButton,
} from '~/pages/Home/Banners/BannerControls'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'
import {
  growingBannerStyles,
  growingBannerTextRowStyles,
} from '~/pages/Home/Banners/utils/growingBannerStyles'
import { AnalyticsNamespace, SharedAnalyticsEvents } from '~/types/Analytics'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { SettingsType } from '~/types/Settings'
import { isElectron } from '~/utils/common'

import { gradients } from '@mangovoice/design-system'

interface WrapperProps {
  $show: boolean
  $hasJustGranted: boolean
}

const Wrapper = styled.div<WrapperProps>`
  ${growingBannerStyles()}
  padding-inline: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  justify-content: space-between;
  background-color: ${({ $hasJustGranted }) =>
    $hasJustGranted
      ? 'var(--chakra-colors-green-solid)'
      : 'var(--chakra-colors-transparent)'};
  background-image: ${({ $hasJustGranted }) =>
    !$hasJustGranted && gradients.mangoBrand.notification_banner};
  align-items: center;
  transition: background-color 200ms ease;
`

// The Enable button keeps the page's line height, taller than the prompt's,
// so the two are aligned by text baseline rather than by box.
const MessageRow = styled.div`
  ${growingBannerTextRowStyles}
  gap: 0;
  align-items: baseline;
`

const SuccessText = styled(Text)`
  color: var(--chakra-colors-brand-contrast);
  display: flex;
  white-space: pre-wrap;
`
interface NotificationsBannerState {
  lastDate?: Date | undefined
}
const notificationsBannerStateAtom = atomWithStorage<NotificationsBannerState>(
  StorageKeys.NotificationsBanner,
  {},
)
const NotificationsBanner = ({ onShowCallback }: BannerProps) => {
  const { t } = useTranslation()
  const [notificationsBannerState, setNotificationsBannerState] = useAtom(
    notificationsBannerStateAtom,
  )
  const { hasGranted, requestPermission, sendSampleNotification } =
    useNotifications()
  const { track } = useAnalytics(AnalyticsNamespace.SHARED)
  const navigate = useNavigate()

  const [show, setShow] = useState(false)
  const [hasJustGranted, setHasJustGranted] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleSetShow = useCallback(
    (val: boolean) => {
      setShow(val)
      onShowCallback(val)
    },
    [onShowCallback],
  )

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const shouldShowBanner = () => {
      if (isElectron) {
        return false
      }

      const lastDate = notificationsBannerState.lastDate

      if (!lastDate && !hasGranted) {
        return true
      }

      const lastDateTimeStamp = lastDate
        ? new Date(lastDate).getTime()
        : new Date().getTime()

      const currentTimeStamp = new Date().getTime()

      const hasBeenDaySinceLastDismiss =
        currentTimeStamp - lastDateTimeStamp > times.dayInMilliseconds

      return hasBeenDaySinceLastDismiss && !hasGranted
    }

    if (shouldShowBanner() && !show) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Set up a new timeout
      timeoutRef.current = setTimeout(() => {
        handleSetShow(true)
        timeoutRef.current = null
      }, 2000)
    }

    // Clean up when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [hasGranted, notificationsBannerState.lastDate, show, handleSetShow])

  const dismissBanner = () => {
    setNotificationsBannerState({
      lastDate: new Date(),
    })
    handleSetShow(false)
  }

  const handleRequestPermission = async () => {
    track(SharedAnalyticsEvents.NotificationBannerEnableClicked)
    const permission = await requestPermission()

    if (permission == 'granted') {
      track(SharedAnalyticsEvents.NotificationBannerGranted)
      setHasJustGranted(true)
      sendSampleNotification()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      // Set up a new timeout for hiding
      timeoutRef.current = setTimeout(() => {
        handleSetShow(false)
        timeoutRef.current = null
      }, 2000)
    } else {
      track(SharedAnalyticsEvents.NotificationBannerBlocked)
      handleSetShow(false)
    }
  }

  return (
    <Wrapper $hasJustGranted={hasJustGranted} $show={show}>
      <MessageRow>
        {hasJustGranted ? (
          <SuccessText>
            {t(
              'notificationsBanner.grantedPrefix',
              'Fantastic, notification preferences can be adjusted in',
            )}
            &nbsp;
            <BannerTextButton
              onClick={() =>
                navigate(
                  `${ApplicationRoutes.SETTINGS}/${SettingsType.NOTIFICATIONS}`,
                )
              }
            >
              <Text
                as="span"
                color="var(--chakra-colors-brand-contrast)"
                style={{
                  display: 'inline-block',
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                {t('notificationsBanner.settings', 'settings')}
              </Text>
            </BannerTextButton>
            .
          </SuccessText>
        ) : (
          <>
            <Text color="var(--chakra-colors-brand-contrast)">
              {t(
                'notificationsBanner.prompt',
                'Mango works best with notifications to keep you up to date.',
              )}
              &nbsp;{' '}
            </Text>

            <BannerTextButton onClick={() => handleRequestPermission()}>
              <Text
                as="span"
                color="var(--chakra-colors-brand-contrast)"
                style={{
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                {t('notificationsBanner.enable', 'Enable Notifications!')}
              </Text>
            </BannerTextButton>
          </>
        )}
      </MessageRow>
      <BannerIconButton
        aria-label={t('tooltips.dismiss', 'Dismiss')}
        onClick={() => dismissBanner()}
      >
        <Icon boxSize="20px" color="brand.contrast" aria-hidden>
          <X size="100%" />
        </Icon>
      </BannerIconButton>
    </Wrapper>
  )
}

export default NotificationsBanner
